import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Eye, Heart, MessageSquare, Share2,
  RefreshCw, Loader2, Linkedin, Globe, ArrowUp, ArrowDown,
  MousePointerClick, Bookmark, ExternalLink, ChevronDown, ChevronUp, Users
} from 'lucide-react';

const API = '/api';

const FB_ICON = () => <svg className="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
const IG_ICON = () => <svg className="h-4 w-4 text-pink-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>;

function PlatformIcon({ platform }) {
  switch (platform) {
    case 'facebook': return <FB_ICON />;
    case 'instagram': return <IG_ICON />;
    case 'linkedin': return <Linkedin className="h-4 w-4 text-blue-700" />;
    case 'wordpress': return <Globe className="h-4 w-4 text-sky-700" />;
    default: return <Globe className="h-4 w-4 text-slate-400" />;
  }
}

function KpiCard({ icon: Icon, label, value, sub, color = 'slate' }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 hover:shadow-sm transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-lg bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`h-3.5 w-3.5 text-${color}-600`} />
        </div>
        <span className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900 tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function SocialAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [syncing, setSyncing] = useState(false);
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const fetchAnalytics = async (period) => {
    const d = period || days;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/social/analytics/dashboard?days=${d}`, { withCredentials: true });
      setData(res);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAnalytics(); }, []);

  const changePeriod = (d) => {
    setDays(d);
    fetchAnalytics(d);
  };

  const syncInsights = async () => {
    setSyncing(true);
    try {
      const { data: res } = await axios.post(`${API}/social/insights/sync-all`, {}, { withCredentials: true });
      if (res.token_expired) {
        toast.error('Meta token expired — update your access token in Connections');
      } else {
        const parts = [];
        if (res.page_insights_synced > 0) parts.push(`${res.page_insights_synced} page insights`);
        if (res.post_insights_synced > 0) parts.push(`${res.post_insights_synced} posts`);
        toast.success(parts.length > 0 ? `Synced ${parts.join(' + ')}` : 'Sync complete');
      }
      fetchAnalytics();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data) return null;

  const { kpis, platform_breakdown, timeline, post_performance, platform_details, last_synced_at, content_by_status } = data;
  const posts = post_performance || [];
  const visiblePosts = showAllPosts ? posts : posts.slice(0, 10);

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="social-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Social Analytics</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Performance across all connected platforms
            {last_synced_at && <span className="ml-2 text-slate-400">Last synced: {new Date(last_synced_at).toLocaleString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map(d => (
            <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'}
              className="h-7 text-xs" onClick={() => changePeriod(d)} data-testid={`period-${d}`}>
              {d}D
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={syncInsights} disabled={syncing}
            className="h-7 text-xs ml-1" data-testid="sync-insights-btn">
            <RefreshCw className={`h-3 w-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-grid">
        <KpiCard icon={Eye} label="Impressions" value={kpis.total_impressions} color="blue" />
        <KpiCard icon={Users} label="Reach" value={kpis.total_reach} color="indigo" />
        <KpiCard icon={Heart} label="Likes" value={kpis.total_likes} color="rose" />
        <KpiCard icon={MessageSquare} label="Comments" value={kpis.total_comments} color="amber" />
        <KpiCard icon={Share2} label="Shares" value={kpis.total_shares} color="emerald" />
        <KpiCard icon={TrendingUp} label="Eng. Rate" value={`${kpis.engagement_rate}%`} color="violet"
          sub={`${kpis.total_engagement.toLocaleString()} total engagements`} />
      </div>

      {/* Platform Breakdown Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="platform-cards">
        {/* Facebook */}
        <Card
          className={`border-l-4 border-l-blue-500 cursor-pointer transition-all hover:shadow-md ${selectedPlatform === 'facebook' ? 'ring-2 ring-blue-400 shadow-md' : ''}`}
          data-testid="platform-card-facebook"
          onClick={() => setSelectedPlatform(selectedPlatform === 'facebook' ? null : 'facebook')}
        >
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FB_ICON />
                <CardTitle className="text-sm">Facebook</CardTitle>
              </div>
              <Badge variant="outline" className="text-[10px]">{platform_details?.facebook?.posts || 0} posts tracked</Badge>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {platform_breakdown?.facebook ? (
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-lg font-bold text-slate-900">{(platform_breakdown.facebook.impressions || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500">Impressions</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{(platform_breakdown.facebook.engagement || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500">Engagement</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{platform_breakdown.facebook.published || 0}</p>
                  <p className="text-[10px] text-slate-500">Published</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-3">No Facebook data yet. Connect and sync.</p>
            )}
          </CardContent>
        </Card>

        {/* Instagram */}
        <Card
          className={`border-l-4 border-l-pink-500 cursor-pointer transition-all hover:shadow-md ${selectedPlatform === 'instagram' ? 'ring-2 ring-pink-400 shadow-md' : ''}`}
          data-testid="platform-card-instagram"
          onClick={() => setSelectedPlatform(selectedPlatform === 'instagram' ? null : 'instagram')}
        >
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IG_ICON />
                <CardTitle className="text-sm">Instagram</CardTitle>
              </div>
              <Badge variant="outline" className="text-[10px]">{platform_details?.instagram?.posts || 0} posts tracked</Badge>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {platform_breakdown?.instagram ? (
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-lg font-bold text-slate-900">{(platform_breakdown.instagram.impressions || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500">Impressions</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{(platform_breakdown.instagram.engagement || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500">Engagement</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{platform_breakdown.instagram.published || 0}</p>
                  <p className="text-[10px] text-slate-500">Published</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-3">No Instagram data yet. Connect and sync.</p>
            )}
          </CardContent>
        </Card>

        {/* LinkedIn */}
        <Card
          className={`border-l-4 border-l-blue-700 cursor-pointer transition-all hover:shadow-md ${selectedPlatform === 'linkedin' ? 'ring-2 ring-blue-600 shadow-md' : ''}`}
          data-testid="platform-card-linkedin"
          onClick={() => setSelectedPlatform(selectedPlatform === 'linkedin' ? null : 'linkedin')}
        >
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Linkedin className="h-4 w-4 text-blue-700" />
                <CardTitle className="text-sm">LinkedIn</CardTitle>
              </div>
              <Badge variant="outline" className="text-[10px]">{platform_details?.linkedin?.posts || 0} posts tracked</Badge>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {platform_breakdown?.linkedin ? (
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-lg font-bold text-slate-900">{(platform_breakdown.linkedin.impressions || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500">Impressions</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{(platform_breakdown.linkedin.engagement || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500">Engagement</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{platform_breakdown.linkedin.published || 0}</p>
                  <p className="text-[10px] text-slate-500">Published</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-3">No LinkedIn data yet. Connect and sync.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Platform Drilldown — shows posts filtered by selected platform */}
      {selectedPlatform && posts.length > 0 && (() => {
        const platformPosts = posts.filter(p => p.platform === selectedPlatform);
        const platformLabel = selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1);
        const borderColor = selectedPlatform === 'facebook' ? 'border-l-blue-500' : selectedPlatform === 'instagram' ? 'border-l-pink-500' : 'border-l-blue-700';
        return (
          <Card className={`border-l-4 ${borderColor}`} data-testid={`platform-drilldown-${selectedPlatform}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <PlatformIcon platform={selectedPlatform} />
                  {platformLabel} Posts ({platformPosts.length})
                </CardTitle>
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setSelectedPlatform(null)} data-testid="close-drilldown">
                  Close
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {platformPosts.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No posts tracked for {platformLabel}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`${selectedPlatform}-post-table`}>
                    <thead>
                      <tr className="text-[10px] uppercase text-slate-400 border-b border-slate-100">
                        <th className="text-left py-2 pr-3 font-medium">Post</th>
                        <th className="text-left py-2 pr-3 font-medium">Date</th>
                        <th className="text-right py-2 px-2 font-medium"><div className="flex items-center justify-end gap-1"><Heart className="h-3 w-3" /> Likes</div></th>
                        <th className="text-right py-2 px-2 font-medium"><div className="flex items-center justify-end gap-1"><MessageSquare className="h-3 w-3" /> Comments</div></th>
                        <th className="text-right py-2 px-2 font-medium"><div className="flex items-center justify-end gap-1"><Share2 className="h-3 w-3" /> Shares</div></th>
                        <th className="text-right py-2 px-2 font-medium"><div className="flex items-center justify-end gap-1"><Eye className="h-3 w-3" /> Impressions</div></th>
                        <th className="text-right py-2 pl-2 font-medium"><div className="flex items-center justify-end gap-1"><TrendingUp className="h-3 w-3" /> Engagement</div></th>
                      </tr>
                    </thead>
                    <tbody>
                      {platformPosts.map((post, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`drilldown-row-${i}`}>
                          <td className="py-2.5 pr-3 max-w-[300px]">
                            <p className="text-xs text-slate-700 truncate" title={post.message}>{post.message || '(no caption)'}</p>
                          </td>
                          <td className="py-2.5 pr-3 text-xs text-slate-400 whitespace-nowrap">{post.date || '—'}</td>
                          <td className="py-2.5 px-2 text-right">
                            <span className={`text-xs font-medium ${post.likes > 0 ? 'text-rose-600' : 'text-slate-300'}`}>{post.likes.toLocaleString()}</span>
                          </td>
                          <td className="py-2.5 px-2 text-right">
                            <span className={`text-xs font-medium ${post.comments > 0 ? 'text-amber-600' : 'text-slate-300'}`}>{post.comments.toLocaleString()}</span>
                          </td>
                          <td className="py-2.5 px-2 text-right">
                            <span className={`text-xs font-medium ${post.shares > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>{post.shares.toLocaleString()}</span>
                          </td>
                          <td className="py-2.5 px-2 text-right">
                            <span className={`text-xs font-medium ${post.impressions > 0 ? 'text-blue-600' : 'text-slate-300'}`}>{(post.impressions || 0).toLocaleString()}</span>
                          </td>
                          <td className="py-2.5 pl-2 text-right">
                            <span className="text-xs font-bold text-slate-900">{post.engagement.toLocaleString()}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Engagement Timeline */}
      {timeline && timeline.length > 0 && (
        <Card data-testid="engagement-timeline">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-slate-500" /> Engagement Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-40 flex items-end gap-[2px]" data-testid="timeline-chart">
              {timeline.slice(-30).map((day, i) => {
                const maxEng = Math.max(...timeline.map(t => (t.engagement || 0) + (t.impressions || 0)), 1);
                const engHeight = Math.max(((day.engagement || 0) / maxEng) * 100, 2);
                const impHeight = Math.max(((day.impressions || 0) / maxEng) * 100, 2);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end gap-[1px] group relative">
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                      {day.date}: {(day.impressions || 0).toLocaleString()} imp, {(day.engagement || 0).toLocaleString()} eng
                    </div>
                    <div className="w-full bg-blue-400/30 rounded-t-[2px]" style={{ height: `${impHeight}%` }} />
                    <div className="w-full bg-emerald-500 rounded-t-[2px]" style={{ height: `${engHeight}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3 text-[10px] text-slate-400">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-400/30 rounded" /> Impressions</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded" /> Engagement</span>
              </div>
              <span className="text-[10px] text-slate-400">{days}-day view</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Post Performance Table */}
      <Card data-testid="post-performance">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-slate-500" /> Post Performance
              <Badge variant="outline" className="text-[10px] ml-1">{posts.length} posts</Badge>
            </CardTitle>
          </div>
          <p className="text-[10px] text-slate-400">Individual post engagement sorted by total engagement</p>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <div className="text-center py-8">
              <BarChart3 className="h-8 w-8 mx-auto mb-2 text-slate-200" />
              <p className="text-sm text-slate-500">No post data yet</p>
              <p className="text-xs text-slate-400 mt-1">Click "Sync" to pull engagement data from your connected platforms</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="post-table">
                  <thead>
                    <tr className="text-[10px] uppercase text-slate-400 border-b border-slate-100">
                      <th className="text-left py-2 pr-3 font-medium">Platform</th>
                      <th className="text-left py-2 pr-3 font-medium">Post</th>
                      <th className="text-left py-2 pr-3 font-medium">Date</th>
                      <th className="text-right py-2 px-2 font-medium">
                        <div className="flex items-center justify-end gap-1"><Heart className="h-3 w-3" /> Likes</div>
                      </th>
                      <th className="text-right py-2 px-2 font-medium">
                        <div className="flex items-center justify-end gap-1"><MessageSquare className="h-3 w-3" /> Comments</div>
                      </th>
                      <th className="text-right py-2 px-2 font-medium">
                        <div className="flex items-center justify-end gap-1"><Share2 className="h-3 w-3" /> Shares</div>
                      </th>
                      <th className="text-right py-2 pl-2 font-medium">
                        <div className="flex items-center justify-end gap-1"><TrendingUp className="h-3 w-3" /> Total</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePosts.map((post, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`post-row-${i}`}>
                        <td className="py-2.5 pr-3">
                          <div className="flex items-center gap-1.5">
                            <PlatformIcon platform={post.platform} />
                            <span className="text-xs capitalize text-slate-600">{post.platform}</span>
                          </div>
                        </td>
                        <td className="py-2.5 pr-3 max-w-[250px]">
                          <p className="text-xs text-slate-700 truncate" title={post.message}>
                            {post.message || '(no caption)'}
                          </p>
                        </td>
                        <td className="py-2.5 pr-3 text-xs text-slate-400 whitespace-nowrap">{post.date || '—'}</td>
                        <td className="py-2.5 px-2 text-right">
                          <span className={`text-xs font-medium ${post.likes > 0 ? 'text-rose-600' : 'text-slate-300'}`}>
                            {post.likes.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right">
                          <span className={`text-xs font-medium ${post.comments > 0 ? 'text-amber-600' : 'text-slate-300'}`}>
                            {post.comments.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right">
                          <span className={`text-xs font-medium ${post.shares > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>
                            {post.shares.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-2.5 pl-2 text-right">
                          <span className="text-xs font-bold text-slate-900">{post.engagement.toLocaleString()}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {posts.length > 10 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-full mt-2 text-xs text-slate-500"
                  onClick={() => setShowAllPosts(!showAllPosts)}
                  data-testid="show-all-posts-btn"
                >
                  {showAllPosts ? (
                    <><ChevronUp className="h-3 w-3 mr-1" /> Show Less</>
                  ) : (
                    <><ChevronDown className="h-3 w-3 mr-1" /> Show All {posts.length} Posts</>
                  )}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Content Status + WhatsApp */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Content by Status */}
        {content_by_status && (
          <Card data-testid="content-by-status">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Content Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(content_by_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                    <span className="text-xs capitalize text-slate-600">{status.replace(/_/g, ' ')}</span>
                    <span className="text-sm font-bold text-slate-900">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Metrics */}
        <Card data-testid="additional-metrics">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Additional Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <MousePointerClick className="h-3.5 w-3.5 text-blue-500" />
                  <span className="text-xs text-slate-600">Link Clicks</span>
                </div>
                <span className="text-sm font-bold text-slate-900">{(kpis.total_link_clicks || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <Bookmark className="h-3.5 w-3.5 text-purple-500" />
                  <span className="text-xs text-slate-600">Saves</span>
                </div>
                <span className="text-sm font-bold text-slate-900">{(kpis.total_saves || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <ExternalLink className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-xs text-slate-600">Publications</span>
                </div>
                <span className="text-sm font-bold text-slate-900">{kpis.total_publications || 0}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <BarChart3 className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-xs text-slate-600">Total Content</span>
                </div>
                <span className="text-sm font-bold text-slate-900">{kpis.total_content || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
